import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { useGetAllVendorsHttp } from "../../../hooks/http/admin/vendors/get-all-vendors.admin.http";
import { useToast } from "../../../hooks/use-toast";
import { handleHttpError } from "../../../utils/utils";
import type { IVendor } from "../../../types/models/vendor.modals.types";
import { ArrowRight } from "lucide-react";
import Spinner from "../../../components/spinner.components";
import { DataTableComponents } from "../../../components/tables/table.components";
import {
  type ColumnDef,
  type ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import useVendorsStore from "../../../store/vendors.store";

interface VendorsListAdminPartialProps {
  search: string;
  searching: boolean;
  vendors?: IVendor[];
}

export default function VendorsListAdminPartial({
  search,
  searching,
  vendors: vendorsProp,
}: VendorsListAdminPartialProps) {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const { toast } = useToast();
  const { setData: setVendors, data: vendors } = useVendorsStore();

  const { execute: getAllVendorsHttp, fetching: fetchingVendors } =
    useGetAllVendorsHttp();

  async function fetchVendors() {
    const response = await getAllVendorsHttp({
      body: null,
    });

    if (!response.success) {
      return handleHttpError(response, toast);
    }

    setVendors(response.data.vendors);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    fetchVendors();
  }, []);

  // Table columns configuration
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo<ColumnDef<IVendor>[]>(
    () => [
      {
        header: "Vendor name",
        accessor: "name",
        cell: ({ row }) => {
          const { name } = row.original;
          return (
            <div className="flex items-center gap-3 font-semibold tracking-wider">
              {name}
            </div>
          );
        },
      },
      {
        header: "Type",
        accessor: "vendorType",
        cell: ({ row }) => {
          const { vendorType: type } = row.original;
          return (
            <div className="flex items-center gap-3 font-semibold capitalize tracking-wider">
              {type}
            </div>
          );
        },
      },

      {
        header: " ",
        accessor: "id",
        cell: ({ row }) => (
          <Link
            className="flex justify-end items-center"
            to={`/admin/vendors/${row.original.id}/overview`}
          >
            <Button
              variant="secondary"
              className="flex items-center gap-2 bg-tertiarybuttonbg hover:bg-white/10 px-4 py-2 border border-border rounded-lg text-white transition-colors"
            >
              View details
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: search.trim().length > 0 ? (vendorsProp as IVendor[]) : vendors || [],
    columns,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    manualPagination: true,
  });

  if (fetchingVendors || searching) {
    return (
      <div className="flex justify-center items-center py-20 w-full">
        <div className="flex items-center gap-2">
          <Spinner width={20} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 pt-6 w-full">
      <DataTableComponents
        table={table as ReturnType<typeof useReactTable>}
        columns={columns as ColumnDef<IVendor>[]}
        setPageNo={() => {}}
        loading={false}
        setSortBy={() => {}}
        tableType="vendor"
      />
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
      <div id="getVendors" onClick={fetchVendors} className="hidden" />
    </div>
  );
}
